// src/modules/app-views/crm/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, FloatButton, Tooltip, Modal } from 'antd';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import { useDocumentContext } from 'components/core/DocumentProvider';
import useZustand from 'hooks/useZustand';
import useFormInstance from 'hooks/useFormInstance';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import translateX from 'utils/translateX';
import { buildInitialValues } from 'utils/mock';
import {
  CalendarOutlined,
  FormOutlined,
  HistoryOutlined,
  InboxOutlined,
  PhoneOutlined,
  PlayCircleOutlined,
  RocketOutlined,
  SwapOutlined,
  UnorderedListOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { FaArrowUpRightDots } from 'react-icons/fa6';
import { TbArrowBackUp } from 'react-icons/tb';
import { AiOutlineTrophy, AiOutlineDislike } from 'react-icons/ai';
import showTransferOpportunityModal from './../../pages/Kanban/modals/TransferOpportunityModal';
import { buildOriginIcon } from 'components/JarvislyComponents/DocumentInfoIcons';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import crmService from '../../service';
import { useUserRole } from '../../useUserRole';
import appService from 'services/appService';
import omnichannelService from 'modules/app-views/omnichannel/service';
import { getPhoneMeta } from 'utils/phone';
import LeftDrawer from 'layouts/LeftDrawer';
import RescheduleModal from './modals/RescheduleModal';
import RecordTab from './tabs/RecordTab';
import FollowUpTab from './tabs/FollowUpTab';
import ActivitiesTab from './tabs/ActivitiesTab';
import HistoryTab from './tabs/HistoryTab';
import { RiChatFollowUpLine } from 'react-icons/ri';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CrmDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    tab,
    initTab,
    handleTabChange,
    availableTabs,
    buildTabs,
    data,
    viewMode,
    refresh,
    registerTabFieldPrefixes,
  } = useDocumentContext();

  const storedCurrentPage = useZustand('module', 'currentPage');
  const storedUser = useZustand('auth', 'user');
  const storedParameters = useZustand('app', 'parameters');

  // console.log('data', data);
  // console.log('data', JSON.stringify(data));

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, submitForm } = useFormInstance({ data });
  const { canManage } = useUserRole();
  const location = useLocation();
  const _id = urlInfo?.params?._id;
  // Holds a post-save callback (e.g., move stage) executed after successful handleFinish
  const afterSaveRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // Attendance state drives view lock: only editable when attendance is started and not filed
  const isNewDocument = viewMode === 'add';
  const isFiled = !!data?._metadata?.audit?.filedAt;
  const isRescheduled = !!data?.attendance?.rescheduledAt && !isFiled;
  const isAttending = !!data?.attendance?.startedAt && !isFiled && !isRescheduled;
  const isDealClosed = data?.status === 'contract_closed' || data?.status === 'lost';
  // Force 'edit' when attending (and not finalized) so all tabs/fields stay editable
  const effectiveViewMode = isNewDocument ? viewMode : (isAttending && !isDealClosed) ? 'edit' : 'view';

  // Board columns for advance/regress stage navigation
  const boards = storedParameters?.modules?.crm?.boards || [];
  const currentBoard = boards.find(b => b.uid === data?.boardUid);
  const sortedColumns = [...(currentBoard?.columns || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );
  const currentColumnIndex = sortedColumns.findIndex(c => c.uid === data?.columnUid);
  const currentColumn = sortedColumns[currentColumnIndex];
  const isOnFinalColumn = !!currentColumn?.final;
  const canAdvance = isAttending && !isOnFinalColumn && !isDealClosed && currentColumnIndex >= 0 && currentColumnIndex < sortedColumns.length - 1;
  const canRegress = isAttending && !isDealClosed && currentColumnIndex > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [whatsappDrawerOpen, setWhatsappDrawerOpen] = useState(false);
  const [whatsappConversationId, setWhatsappConversationId] = useState(null);
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(false);
  // Phone resolved from entity contacts — passed to LeftDrawer for source-bound switching
  const [whatsappPhone, setWhatsappPhone] = useState(null);
  const [whatsappCountryCode, setWhatsappCountryCode] = useState('BR');
  // True when the document was loaded but no WhatsApp-capable phone was found in entity contacts
  const [whatsappHasNoPhone, setWhatsappHasNoPhone] = useState(false);

  // Merge navigation state (boardUid, columnUid from Kanban "+" button) into initial values
  const kanbanState = location?.state || {};
  const initialValues = useMemo(
    () => ({
      ...buildInitialValues(isMockStep, data),
      ...(kanbanState.boardUid && !data ? { boardUid: kanbanState.boardUid } : {}),
      ...(kanbanState.columnUid && !data ? { columnUid: kanbanState.columnUid } : {}),
    }),
    [data]
  );

  const requiredFields = useMemo(() => {
    return ['opportunity'];
  }, [storedCurrentPage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const childProps = { ...props, data, initTab, viewMode };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, data]);

  // Register tab field prefixes so validation errors navigate to the correct tab
  // Prefix '' acts as catch-all: any field not starting with 'formData' falls to initTab (record)
  // 'formData' (longer prefix) always wins for FollowUp tab fields
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({
      formData: 'followup',
      '': initTab,
    });
  }, [registerTabFieldPrefixes, initTab]);

  // Pre-load WhatsApp conversation as soon as the document is available so the drawer
  // renders without a flash when the user clicks the WhatsApp float button.
  useEffect(() => {
    if (!data?._id || isNewDocument) return;

    const { phone, countryCode } = resolveWhatsappPhone(data);

    if (!phone) {
      setWhatsappHasNoPhone(true);
      return;
    }

    setWhatsappHasNoPhone(false);
    // Normalize to whatsappNumber format (e.g. 5519982320000) so the backend finds
    // the existing conversation created by the Meta webhook, which uses this format.
    const normalizedPhone = getPhoneMeta(phone, countryCode)?.whatsappNumber || phone;
    setWhatsappPhone(normalizedPhone);
    setWhatsappCountryCode(countryCode);

    // Fire-and-forget — silently set conversationId before user clicks.
    // Restore the preferred source (Baileys or Meta) the agent last used.
    const preferredSourceId = localStorage.getItem('omnichannel_preferred_source') || undefined;
    omnichannelService
      .getOrCreateByPhone({
        phone: normalizedPhone,
        countryCode,
        ...(preferredSourceId ? { sourceId: preferredSourceId } : {}),
      })
      .then(result => {
        const conversationId = result?._id || result?.[0]?._id;
        if (conversationId) setWhatsappConversationId(conversationId);
      })
      .catch(() => {});
  }, [data?._id]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      const docId = values?._id || allValues?._id || data?._id;

      // Strip fields managed by dedicated APIs (addFollowUp, startAttendance, etc.)
      // and populated references — these are set on the form by SimpleFormProviderInitializer
      // via form.setFieldsValue(entireDocument) and would overwrite backend data if sent.
      const {
        _id,
        _metadata,
        followUp,
        attendance,
        __entity,
        __personContact,
        ...dataToSave
      } = values;

      await upsert({ dataSet: dataToSave, _id: docId });

      // Execute post-save callback (e.g., move stage) if pending
      if (afterSaveRef.current) {
        const callback = afterSaveRef.current;
        afterSaveRef.current = null;
        await callback();
      }
    } catch (err) {
      afterSaveRef.current = null;
      throw err;
    }
  };

  const handleStartAttendance = async () => {
    try {
      await crmService.startAttendance(data._id, {
        accountName: storedUser?.name || null,
        accountAvatarUrl:
          storedUser?.avatar?.base64Thumb ||
          storedUser?.avatar?.thumbUrl ||
          null,
        followUpText: translateX('attendance_started'),
      });
      appService.message({ flag: 's', text: 'attendance_started_success' });
      refresh?.();
    } catch (err) {
      console.error('[CrmDocument] startAttendance error:', err);
      appService.message({ flag: 'e', text: 'error_starting_attendance' });
    }
  };

  const handleOpenWhatsApp = async () => {
    // If pre-load already resolved a conversationId, open immediately
    if (whatsappConversationId) {
      setWhatsappDrawerOpen(true);
      return;
    }

    // Fallback: resolve on-demand (e.g. pre-load failed or no phone at mount time)
    const { phone, countryCode } = resolveWhatsappPhone(data);

    if (!phone) {
      appService.message({ flag: 'w', text: 'no_whatsapp_phone' });
      return;
    }

    setLoadingWhatsapp(true);
    try {
      const normalizedPhone = getPhoneMeta(phone, countryCode)?.whatsappNumber || phone;
      const preferredSourceId = localStorage.getItem('omnichannel_preferred_source') || undefined;
      const result = await omnichannelService.getOrCreateByPhone({
        phone: normalizedPhone,
        countryCode,
        ...(preferredSourceId ? { sourceId: preferredSourceId } : {}),
      });
      const conversationId = result?._id || result?.[0]?._id;
      if (conversationId) {
        setWhatsappPhone(normalizedPhone);
        setWhatsappCountryCode(countryCode);
        setWhatsappConversationId(conversationId);
        setWhatsappDrawerOpen(true);
      }
    } catch (err) {
      console.error('[CrmDocument] getOrCreateByPhone error:', err);
      appService.message({ flag: 'e', text: 'error_opening_whatsapp' });
    } finally {
      setLoadingWhatsapp(false);
    }
  };

  const handleReschedule = () => {
    setRescheduleModalOpen(true);
  };

  const handleMoveStage = async (direction) => {
    const targetIndex = direction === 'advance' ? currentColumnIndex + 1 : currentColumnIndex - 1;
    const targetColumn = sortedColumns[targetIndex];
    if (!targetColumn) {
      appService.message({
        flag: 'w',
        text: direction === 'advance' ? 'already_last_column' : 'already_first_column',
      });
      return;
    }

    // Set post-save callback: after successful save, move to the target column
    const fromColumnTitle = currentColumn?.title || '?';
    afterSaveRef.current = async () => {
      try {
        await crmService.moveOpportunity(data._id, {
          boardUid: data.boardUid,
          columnUid: targetColumn.uid,
        });
        // Log movement in follow-up timeline
        const followUpKey = direction === 'advance' ? 'followup_stage_advanced' : 'followup_stage_regressed';
        await crmService.addMovementFollowUp(data._id, {
          user: storedUser,
          text: translateX(followUpKey, { from: fromColumnTitle, to: targetColumn.title }),
        });
        const msg = direction === 'advance' ? 'stage_advanced' : 'stage_regressed';
        appService.message({ flag: 's', text: translateX(msg, { column: targetColumn.title }) });
        refresh?.();
      } catch (err) {
        console.error('[CrmDocument] moveStage error:', err);
        appService.message({ flag: 'e', text: 'error' });
      }
    };

    // Trigger full save flow: validates all fields (with tab switch + focus on error),
    // then calls handleFinish which executes the afterSaveRef callback on success
    submitForm?.();
  };

  const handleFileOpportunity = () => {
    Modal.confirm({
      title: translateX('file_opportunity'),
      content: translateX('file_opportunity_confirm'),
      okText: translateX('yes'),
      cancelText: translateX('no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await crmService.fileOpportunity(data._id);
          appService.message({ flag: 's', text: 'opportunity_filed_success' });
          refresh?.();
        } catch (err) {
          console.error('[CrmDocument] fileOpportunity error:', err);
          appService.message({ flag: 'e', text: 'error_filing_opportunity' });
        }
      },
    });
  };

  const handleCloseDeal = () => {
    Modal.confirm({
      title: translateX('close_deal'),
      content: translateX('close_deal_confirm'),
      okText: translateX('yes'),
      cancelText: translateX('no'),
      onOk: async () => {
        try {
          await axiosService.put({
            url: API.CRM.URL.replace(':_id?', data._id),
            data: { status: 'contract_closed' },
            returnAsObject: true,
          });
          await crmService.addMovementFollowUp(data._id, {
            user: storedUser,
            text: translateX('followup_deal_closed'),
          });
          appService.message({ flag: 's', text: 'deal_closed_success' });
          refresh?.();
        } catch (err) {
          console.error('[CrmDocument] closeDeal error:', err);
          appService.message({ flag: 'e', text: 'error' });
        }
      },
    });
  };

  const handleLoseDeal = () => {
    Modal.confirm({
      title: translateX('lose_deal'),
      content: translateX('lose_deal_confirm'),
      okText: translateX('yes'),
      cancelText: translateX('no'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosService.put({
            url: API.CRM.URL.replace(':_id?', data._id),
            data: { status: 'lost' },
            returnAsObject: true,
          });
          await crmService.addMovementFollowUp(data._id, {
            user: storedUser,
            text: translateX('followup_deal_lost'),
          });
          appService.message({ flag: 's', text: 'deal_lost_success' });
          refresh?.();
        } catch (err) {
          console.error('[CrmDocument] loseDeal error:', err);
          appService.message({ flag: 'e', text: 'error' });
        }
      },
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="opportunity"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          locked={effectiveViewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry overlapsBackground data={data} />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>

      {/* Float action buttons — hidden when deal is closed/lost */}
      {!isNewDocument && !isDealClosed &&
        createPortal(
          isAttending ? (
            // State 2: Attending — SpeedDial with sub-actions
            <>
              <style>{`
              .crm-float-group .ant-float-btn-group-list {
                gap: 8px !important;
                margin-bottom: -18px !important;
                padding-bottom: 18px !important;
              }
            `}</style>
              <FloatButton.Group
                trigger="hover"
                type="primary"
                icon={<RocketOutlined />}
                rootClassName="crm-float-group"
                style={{ right: 24, bottom: 24, zIndex: 1000 }}
              >
                {canManage && (
                  <Tooltip title={translateX(canRegress ? 'regress_stage' : 'already_first_column')} placement="left">
                    <FloatButton
                      icon={<TbArrowBackUp />}
                      onClick={() => canRegress && handleMoveStage('regress')}
                      style={canRegress ? undefined : { opacity: 0.35, cursor: 'not-allowed' }}
                    />
                  </Tooltip>
                )}
                {isOnFinalColumn ? (
                  <>
                    <Tooltip title={translateX('close_deal')} placement="left">
                      <FloatButton
                        icon={<AiOutlineTrophy style={{ color: '#52c41a' }} />}
                        onClick={handleCloseDeal}
                      />
                    </Tooltip>
                    <Tooltip title={translateX('lose_deal')} placement="left">
                      <FloatButton
                        icon={<AiOutlineDislike style={{ color: '#ff4d4f' }} />}
                        onClick={handleLoseDeal}
                      />
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip title={translateX(canAdvance ? 'advance_stage' : 'already_last_column')} placement="left">
                    <FloatButton
                      icon={<FaArrowUpRightDots />}
                      onClick={() => canAdvance && handleMoveStage('advance')}
                      style={canAdvance ? undefined : { opacity: 0.35, cursor: 'not-allowed' }}
                    />
                  </Tooltip>
                )}
                <Tooltip title={translateX('call_voip')} placement="left">
                  <FloatButton icon={<PhoneOutlined />} />
                </Tooltip>
                <Tooltip title={translateX('reschedule_opportunity')} placement="left">
                  <FloatButton icon={<CalendarOutlined />} onClick={handleReschedule} />
                </Tooltip>
                <Tooltip title={translateX('send_whatsapp')} placement="left">
                  <FloatButton
                    icon={<WhatsAppOutlined />}
                    loading={loadingWhatsapp}
                    onClick={handleOpenWhatsApp}
                  />
                </Tooltip>
                <Tooltip title={translateX('transfer_opportunity')} placement="left">
                  <FloatButton
                    icon={<SwapOutlined />}
                    onClick={() => showTransferOpportunityModal({
                      card: { _id: data?._id, boardUid: data?.boardUid, columnUid: data?.columnUid, consultantId: data?.consultantId },
                      onSuccess: () => refresh?.(),
                    })}
                  />
                </Tooltip>
                <Tooltip title={translateX('file_opportunity')} placement="left">
                  <FloatButton icon={<InboxOutlined />} onClick={handleFileOpportunity} />
                </Tooltip>
              </FloatButton.Group>
            </>
          ) : (
            // State 1 & 3: Not started or Filed — single "Start Attendance" button
            <Tooltip title={translateX('start_attendance')} placement="left">
              <FloatButton
                type="primary"
                icon={<PlayCircleOutlined />}
                style={{ right: 24, bottom: 24, zIndex: 1000 }}
                onClick={handleStartAttendance}
              />
            </Tooltip>
          ),
          document.body
        )}

      <LeftDrawer
        open={whatsappDrawerOpen}
        onClose={() => setWhatsappDrawerOpen(false)}
        conversationId={whatsappConversationId}
        phone={whatsappPhone}
        countryCode={whatsappCountryCode}
        entityId={data?.entityId}
        noPhone={whatsappHasNoPhone}
        crmId={data?._id}
        entityName={
          data?.['__entity']?.fullName || data?.['__entity']?.name || data?.opportunity || null
        }
      />

      <RescheduleModal
        show={rescheduleModalOpen}
        setShow={setRescheduleModalOpen}
        docId={data?._id}
        opportunityName={data?.opportunity || ''}
        onSuccess={() => {
          setRescheduleModalOpen(false);
          refresh?.();
        }}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CrmDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Resolve the phone to use for WhatsApp, mirroring what CardHeaderColumn2 displays.
// Priority: contactPhone snapshot (stored at lead distribution) → entity contacts.
function resolveWhatsappPhone(data) {
  // 1. CRM snapshot fields (same source as CardHeaderColumn2)
  const snapshotPhone = data?.contactPhone || data?.__lead?.phone || null;
  if (snapshotPhone) {
    return {
      phone: snapshotPhone,
      countryCode: data?.__lead?.countryCode || 'BR',
    };
  }

  // 2. Fallback: entity contacts (prefer canReceiveWhatsApp)
  const contacts = data?.['__entity']?.contacts || [];
  const contact =
    contacts.find(c => c.contactType === 'phone' && c.canReceiveWhatsApp) ||
    contacts.find(c => c.contactType === 'phone');

  return {
    phone: contact?.value || null,
    countryCode: contact?.countryCode || 'BR',
  };
}

function buildTabsArray(props) {
  const { initTab, data, viewMode } = props;
  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      icon: buildOriginIcon(data),
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <RecordTab {...props} tabName={initTab} />,
    },
    {
      key: 'followup',
      icon: <RiChatFollowUpLine />,
      label: <span>{translateX('followup')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <FollowUpTab {...props} />,
    },
    // {
    //   key: 'activities',
    //   icon: <UnorderedListOutlined />,
    //   label: <span>{translateX('activities')}</span>,
    //   hide: false,
    //   disabled: isNewDocument,
    //   children: <ActivitiesTab {...props} />,
    // },
    // {
    //   key: 'history',
    //   icon: <HistoryOutlined />,
    //   label: <span>{translateX('history')}</span>,
    //   hide: false,
    //   disabled: isNewDocument,
    //   children: <HistoryTab {...props} />,
    // },
  ];
}
